import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Mail, Phone, MapPin, AlertTriangle, Check, Shield, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverContactUpdatePage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentInfo, setCurrentInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [form, setForm] = useState({
    new_email: '',
    new_phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    reason: ''
  });

  useEffect(() => {
    fetchCurrentInfo();
  }, [token]);

  const fetchCurrentInfo = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/account/contact-update/${token}`);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid or expired link');
      }

      const data = await response.json();
      setCurrentInfo(data.current_info);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    // Check if any changes were made
    const hasChanges = form.new_email || form.new_phone || 
                       form.address_line1 || form.city || 
                       form.state || form.zip_code;

    if (!hasChanges) {
      setError('Please enter at least one field to update');
      setSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/account/contact-update/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit request');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !currentInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Link Invalid or Expired</h1>
          <p className="text-gray-600 mb-6">
            This contact update link is no longer valid. It may have expired or already been used.
          </p>
          <p className="text-sm text-gray-500">
            If you need to update your contact information, please contact BlueLock Freights directly.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted</h1>
          <p className="text-gray-600 mb-6">
            Thank you. Your request has been received and will be reviewed. If additional verification is required, a team member will contact you.
          </p>
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="contact-update-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Update Contact Information</h1>
          <p className="text-gray-600">
            Use this form to request an update to your contact information for your application or onboarding file. Requests are reviewed before changes take effect.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Section 1: Current Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Current Information</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            This information is currently on file. If something is incorrect, submit an update request below.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Full Name:</span>
              <p className="font-medium text-gray-900">{currentInfo?.full_name || 'Not provided'}</p>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>
              <p className="font-medium text-gray-900">{currentInfo?.email || 'Not provided'}</p>
            </div>
            <div>
              <span className="text-gray-500">Phone:</span>
              <p className="font-medium text-gray-900">{currentInfo?.phone || 'Not provided'}</p>
            </div>
            <div>
              <span className="text-gray-500">Address:</span>
              <p className="font-medium text-gray-900">
                {currentInfo?.address_line1 ? (
                  <>
                    {currentInfo.address_line1}
                    {currentInfo.address_line2 && <>, {currentInfo.address_line2}</>}
                    {currentInfo.city && <>, {currentInfo.city}</>}
                    {currentInfo.state && <> {currentInfo.state}</>}
                    {currentInfo.zip_code && <> {currentInfo.zip_code}</>}
                  </>
                ) : 'Not provided'}
              </p>
            </div>
          </div>
        </div>

        {/* Section 2: Request Updates Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Requested Updates</h2>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Enter only the items you need to update. Leave fields blank if no change is needed.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Mail className="w-4 h-4 inline mr-1" />
                New Email Address
              </label>
              <input
                type="email"
                value={form.new_email}
                onChange={(e) => setForm({ ...form, new_email: e.target.value })}
                placeholder="new.email@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="new-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">Use an email you can access.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Phone className="w-4 h-4 inline mr-1" />
                New Phone Number
              </label>
              <input
                type="tel"
                value={form.new_phone}
                onChange={(e) => setForm({ ...form, new_phone: e.target.value })}
                placeholder="(###) ###-####"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="new-phone-input"
              />
              <p className="text-xs text-gray-500 mt-1">Include area code.</p>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                New Address (Optional)
              </label>
              <div className="space-y-3">
                <input
                  type="text"
                  value={form.address_line1}
                  onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                  placeholder="Address Line 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="address1-input"
                />
                <input
                  type="text"
                  value={form.address_line2}
                  onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                  placeholder="Address Line 2 (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="address2-input"
                />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="City"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="city-input"
                  />
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    placeholder="State"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="state-input"
                  />
                  <input
                    type="text"
                    value={form.zip_code}
                    onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                    placeholder="ZIP"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    data-testid="zip-input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Optional. Used for record accuracy and operational coordination.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Update (Optional)
              </label>
              <textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder="Example: new number, typo correction, moved recently..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="reason-input"
              />
              <p className="text-xs text-gray-500 mt-1">This helps our team review your request.</p>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
              data-testid="submit-update-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Update Request'}
            </button>
          </div>
        </form>

        {/* Security Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900 mb-2">Security & Privacy Notice</h3>
              <p className="text-sm text-amber-800">
                Do not upload or submit sensitive personal information that is not required for onboarding or verification. This includes Social Security cards or numbers, bank login details, or unrelated identity documents. Only submit the information requested by BlueLock Freights. If additional documentation is required, a team member will contact you.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>BlueLock Freights</p>
          <p>Professional Transportation Services</p>
        </div>
      </div>
    </div>
  );
};

export default DriverContactUpdatePage;
